// Angular
import {
  Component,
  Output,
  EventEmitter,
  OnDestroy,
  Input,
  ViewChild,
  ElementRef,
} from "@angular/core";

import { LoggerService } from "@bfx/logger";
import { BfxIconButtonComponent } from "shared/base-components/bfx-icon-button";
const logger = LoggerService.for(__dirname);

export enum SpinDirection {
  UP,
  DOWN,
}
/**
 * Component for the spinner buttons - handles view and autorepeat functions.
 * Currently for focus, the focus output events are derived from the  system focus events so from a timing
 * persective, the focus events will arrive after the spin events - e.g. a spin UP will arive then a focus event.
 *
 * The focus/blur events are per both buttons - clicking from one to another does not generate focus/blur events
 */
@Component({
  selector: "spinner-buttons",
  templateUrl: "./html/spinner-buttons.html",
  providers: [],
  standalone: false,
})
export class SpinnerButtonsComponent implements OnDestroy {
  private config: {
    pressNHold: {
      mouseTimer: ReturnType<typeof setTimeout>; // Timer - used for keeping onInterval
      initialDelay: number; // Wait for this length before starting repeat
      repeatDelay: number; // Once repeating, interval between each event
    };
  } = {
    pressNHold: {
      mouseTimer: null,
      initialDelay: 500,
      repeatDelay: 100,
    },
  };

  /** Used when focus is lost from both buttons */
  @Output() blur: EventEmitter<any> = new EventEmitter<any>();
  /** USed when focus is gained in either buttons */
  @Output() focus: EventEmitter<any> = new EventEmitter<any>();
  /** Emits spin events */
  @Output() spin: EventEmitter<SpinDirection> = new EventEmitter();

  /** Disable all button and show in disabled state */
  @Input() public disabled = false;

  /** up arrow disabled - for example if max has already been hit */
  @Input() public upDisabled = false;

  /** down arrow disabled - for example if min has already been hit */
  @Input() public downDisabled = false;

  /** Disable all button and show in a locked state */
  @Input() public locked = false;

  /** Reference to up button for checking focus changes */
  @ViewChild("upButton") private upButton: BfxIconButtonComponent;
  /** Reference to down button for checking focus changes */
  @ViewChild("downButton") private downButton: BfxIconButtonComponent;

  public inputFocused = false;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor() {}

  // /**
  //  * If any of the disabled property changes then we may automatically unsubscribe from mouse events
  //  * so need to clear any timer thats active
  //  * @param changes
  //  */
  // ngOnChanges(changes: SimpleChanges): void {
  //   // if (this.config.pressNHold.mouseTimer != null) {
  //   //   let clear = false;
  //   //   for (const propName in changes) {
  //   //     switch (propName) {
  //   //       case "disabled":
  //   //       case "upDisabled":
  //   //       case "downDisabled":
  //   //         clear = true;
  //   //         break;
  //   //       default:
  //   //         break;
  //   //     }
  //   //   }
  //   //   if (clear) {
  //   //     this.clearTimer();
  //   //   }
  //   // }
  // }

  public ngOnDestroy(): void {
    this.clearTimer();
  }

  public spinUp(): void {
    if (!this.upDisabled) {
      // logger.out.info("Pushing UP");
      this.spin.next(SpinDirection.UP);
    }
  }

  public spinDown(): void {
    if (!this.downDisabled) {
      // logger.out.info("Pushing DOWN");
      this.spin.next(SpinDirection.DOWN);
    }
  }

  /**********************************************************************/

  public onFocus(event: MouseEvent): void {
    const nativeUp = this.upButton?.buttonElement?.nativeElement;
    const nativeDown = this.downButton?.buttonElement?.nativeElement;
    if (event.relatedTarget != nativeUp && event.relatedTarget != nativeDown) {
      // logger.out.info("Dispatching focus event");
      // Focus not changing from the other spinner button
      this.focus.next(event);
    }
  }

  public onBlur(event: FocusEvent): void {
    const nativeUp = this.upButton?.buttonElement?.nativeElement;
    const nativeDown = this.downButton?.buttonElement?.nativeElement;

    // Can get a blur event when we hit the max/min and disable a button so need to clear timer here
    if (event.relatedTarget != nativeUp && event.relatedTarget != nativeDown) {
      this.clearTimer();
      // We don't signal a blur if our current button has just become disabled - instead, lets
      // move focus to the other button
      if (event.relatedTarget == null) {
        if (event.target == nativeUp && this.upDisabled && !this.downDisabled) {
          // We've lost focus to our up button which has become disabled
          setTimeout(() => (nativeDown as HTMLButtonElement).focus(), 0);
        } else if (event.target == nativeDown && this.downDisabled && !this.upDisabled) {
          // We've lost focus to our down button which has become disabled
          setTimeout(() => (nativeUp as HTMLButtonElement).focus(), 0);
        } else {
          this.blur.next(event);
        }
      } else {
        // We've moved focus to an element that's not the other spinner button
        this.blur.next(event);
      }
    }
  }

  public onMouseUp(_event: Event): void {
    if (!(this.disabled || this.locked)) {
      this.clearTimer();
      this.inputFocused = false;
      // Do not preventDefault as needed for focusEvents
      // $event.preventDefault();
    }
  }

  public onMouseLeave(_event: Event): void {
    if (!(this.disabled || this.locked)) {
      this.clearTimer();
      // Do not preventDefault as needed for focusEvents
      // $event.preventDefault();
    }
  }

  public onMouseDown($event: Event, dir: boolean): void {
    if (!(this.disabled || this.locked)) {
      this.inputFocused = true;
      this.repeat($event, this.config.pressNHold.initialDelay, dir);
      // Do not preventDefault as needed for focusEvents
      // $event.preventDefault();
    }
  }

  public isFocusTarget(event: FocusEvent): boolean {
    const nativeUp = this.upButton?.buttonElement?.nativeElement;
    const nativeDown = this.downButton?.buttonElement?.nativeElement;
    return event.relatedTarget == nativeUp || event.relatedTarget == nativeDown;
  }

  private repeat(event: Event, interval: number, dir: boolean): void {
    dir ? this.spinUp() : this.spinDown();

    this.clearTimer();
    // And do it all again
    this.config.pressNHold.mouseTimer = setTimeout(() => {
      this.repeat(event, this.config.pressNHold.repeatDelay, dir);
    }, interval);
  }

  private clearTimer(): void {
    if (this.config.pressNHold.mouseTimer) {
      clearInterval(this.config.pressNHold.mouseTimer);
      this.config.pressNHold.mouseTimer = null;
    }
  }
}
